/**
 * Form Parser Utility
 * Parses imported form JSON and converts to Form Studio internal format
 */
import {
    extractSignerAttributeBindingsFromSignatures,
    signerRowsOf,
    tabRectsOf,
    signerKeyFromAttribute,
} from "./signatureUtils";
import { normalizeAdditionalFieldsJSONEntries } from "./additionalFieldsJsonShape";
import { parseImportedSectionGoTo } from "./sectionGoTo";

/**
 * Main parser function
 * @param {string} jsonString - Raw JSON string from import
 * @returns {Object} - Parsed form data or error
 */
/**
 * Every `formUploadData*` array a template carries, in order.
 *
 * `formUploadData` (no suffix) is the first; the rest are suffixed by position.
 * Reads until the first gap rather than stopping at a fixed count — the old
 * code handled only 1..3 and dropped a fourth section outright.
 */
export function collectFormUploadDataArrays(metadata) {
    const arrays = [];
    for (let i = 1; ; i++) {
        const key = i === 1 ? "formUploadData" : `formUploadData${i}`;
        const value = metadata?.[key];
        if (!Array.isArray(value)) break;
        arrays.push(value);
    }
    return arrays;
}

export function parseFormJSON(jsonString) {
    try {
        // Parse JSON
        const formData = JSON.parse(jsonString);
        
        // Validate structure
        const validation = validateFormStructure(formData);
        if (!validation.isValid) {
            return {
                success: false,
                errors: validation.errors,
                warnings: []
            };
        }

        // Extract data
        const sections = parseSections(formData);
        const fields = parseFields(formData, sections);
        const metadata = parseMetadata(formData);

        // Populate formUploadSections.fieldIds by matching attributeNames to parsed field IDs.
        //
        // Read every array present, not a fixed three: templates with a fourth
        // upload section (CO 0002, 0003, 0055, 0056) lost it entirely here.
        //
        // An entry whose attributeName has NO field in this template — these
        // name data gathered elsewhere, e.g. `earnestMoneyHolder` on a Counter
        // Proposal — cannot resolve to a field id. Dropping it silently loses
        // authored data, so the raw name is kept on `attrNames` and the builder
        // re-emits it in place.
        const formUploadDataArrays = collectFormUploadDataArrays(formData.metadata);
        metadata.formUploadSections.forEach((section, idx) => {
            const attrNames = formUploadDataArrays[idx] || [];
            section.attrNames = [...attrNames];
            section.fieldIds = attrNames
                .map(attrName => fields.find(f => f.attributeName === attrName)?.id)
                .filter(Boolean);
        });

        // Parse dealSummaryJSON / formSummaryJSON back into field
        // properties so per-field summary label/type overrides
        // round-trip cleanly through `generateSummaryJSON`. These
        // properties stay INTERNAL — the builder no longer emits
        // them onto field-level output (see `buildPublishedFieldOutput`).
        const dealSummary = formData.dealSummaryJSON || {};
        const formSummary = formData.formSummaryJSON || {};
        fields.forEach(field => {
            if (dealSummary[field.attributeName] !== undefined) {
                const ds = dealSummary[field.attributeName];
                field.dealSummary = ds.display;
                field.dealSummaryLabel = ds.label ?? null;
                field.dealSummaryType = ds.type ?? null;
            }
            if (formSummary[field.attributeName] !== undefined) {
                const fs = formSummary[field.attributeName];
                field.formSummary = fs.display;
                field.formSummaryLabel = fs.label ?? null;
                field.formSummaryType = fs.type ?? null;
            }
        });

        // Collect orphaned summary entries (attributeNames not found in any field)
        const fieldAttrNames = new Set(fields.map(f => f.attributeName));
        const extraFormSummary = {};
        const extraDealSummary = {};
        for (const [key, value] of Object.entries(formSummary)) {
            if (!fieldAttrNames.has(key)) extraFormSummary[key] = value;
        }
        for (const [key, value] of Object.entries(dealSummary)) {
            if (!fieldAttrNames.has(key)) extraDealSummary[key] = value;
        }

        mergePublishedSignerFieldsIntoClientFields(fields, formData.signaturesJSON);
        mergePublishedSignerFieldsIntoClientFields(fields, formData.initialsJSON);
        augmentSignerBindingsWithLinkedFieldIds(metadata.signerAttributeBindings, fields);
        augmentSignerBindingsWithLinkedFieldIds(metadata.initialerAttributeBindings, fields);

        return {
            success: true,
            data: {
                fields,
                sections,
                ...metadata,
                extraFormSummary,
                extraDealSummary,
            },
            warnings: validation.warnings,
            errors: []
        };
    } catch (error) {
        return {
            success: false,
            errors: [`Invalid JSON: ${error.message}`],
            warnings: []
        };
    }
}

/**
 * Validate form structure
 */
function validateFormStructure(formData) {
    const errors = [];
    const warnings = [];

    // Check required top-level fields
    if (!formData.formJSON) {
        errors.push("Missing required field: formJSON");
    }

    if (!formData.formJSON?.formData) {
        errors.push("Missing required field: formJSON.formData");
    }

    if (!formData.formJSON?.formData?.[0]?.sections) {
        errors.push("Missing required field: formJSON.formData[0].sections");
    }

    // Warnings for optional fields
    if (!formData.metadata) {
        warnings.push("No metadata found - form settings will be empty");
    }

    if (!formData.signaturesJSON) {
        warnings.push("No signature roles found");
    }

    return {
        isValid: errors.length === 0,
        errors,
        warnings
    };
}

/**
 * Parse sections from form JSON
 */
function parseSections(formData) {
    const sections = formData.formJSON.formData[0].sections;
    
    return sections.map((section, index) => {
        // Validate required section fields
        if (!section.title) {
            console.warn(`Section at index ${index} missing title`);
        }
        if (!section.attributeName) {
            console.warn(`Section at index ${index} missing attributeName`);
        }

        const { goTo, goToManual } = parseImportedSectionGoTo(section, index, sections);
        return {
            id: `section-${index}`,
            title: section.title || `Section ${index + 1}`,
            titleShort: section.titleShort || null,
            subtitle: section.subtitle || "",
            description: section.description || "",
            attributeName: section.attributeName || `section${index}`,
            type: section.type || "data",
            goTo,
            goToManual,
            info: section.info || null,
            metadata: section.metadata || null,
        };
    });
}

/**
 * Normalize `field.editableAnnotation` from published formJSON (round-trip).
 * @param {*} raw - imported `editableAnnotation` or undefined
 * @returns {Object|null} `{ meviDealRoles: string[], ...passthrough }` or null if absent/invalid
 */
export function normalizeEditableAnnotation(raw) {
    if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
        return null;
    }
    const keys = Object.keys(raw);
    if (keys.length === 0) {
        return null;
    }
    const rolesRaw = raw.meviDealRoles;
    const meviDealRoles = Array.isArray(rolesRaw)
        ? rolesRaw.map((r) => String(r || "").trim()).filter(Boolean)
        : [];
    const { meviDealRoles: _omit, ...rest } = raw;
    return { ...rest, meviDealRoles };
}

/**
 * Merge full published field rows from signaturesJSON signer `fields`[] into parsed form `fields` (by attributeName).
 * Appends a new client field when no match (section = first section in the array).
 */
export function mergePublishedSignerFieldsIntoClientFields(fields, rawSignaturesJSON) {
    if (!rawSignaturesJSON || typeof rawSignaturesJSON !== "object" || !Array.isArray(fields)) return;
    const defaultSectionId = fields[0]?.sectionId || "section-0";
    // A signer block may legitimately carry the same attributeName more than once — GA F522 places
    // Buyer1_SigDate in two spots with different labels and boxes. Merging by attributeName alone
    // folded the second onto the first and lost it. Once a signer-prefixed name has been consumed
    // in this pass, the next occurrence becomes its own client field instead.
    //
    // Only for signer-prefixed names: an unprefixed attribute appearing twice is the section /
    // signer-block duplication that audit-section-signer-field-duplicates.js reports, and merging
    // remains the right behaviour there.
    const consumedSignerAttrs = new Set();
    for (const rv of Object.values(rawSignaturesJSON)) {
        // initialsJSON roles carry `initials`, signaturesJSON roles carry `signatures`.
        const sigs = signerRowsOf(rv);
        for (const sig of sigs) {
            const pFields = Array.isArray(sig.fields) ? sig.fields : [];
            for (const pf of pFields) {
                if (!pf || typeof pf !== "object") continue;
                const an = pf.attributeName;
                if (!an) continue;
                const isSignerPrefixed = !!signerKeyFromAttribute(an);
                const alreadyConsumed = isSignerPrefixed && consumedSignerAttrs.has(an);
                if (isSignerPrefixed) consumedSignerAttrs.add(an);
                const idx = alreadyConsumed ? -1 : fields.findIndex((f) => f.attributeName === an);
                if (idx >= 0) {
                    const cur = fields[idx];
                    const { signers: _omitSig, ...pfSansSigners } = pf;
                    const mergedMeta = { ...(cur.metadata || {}), ...(pfSansSigners.metadata || {}) };
                    if (pf.metadata?.pdfLocation != null) mergedMeta.pdfLocation = pf.metadata.pdfLocation;
                    const pl = pf.metadata?.pdfLocation;
                    const annLines =
                        pl && typeof pl === "object" && !Array.isArray(pl) ? pl.lines ?? 0 : 0;
                    fields[idx] = {
                        ...cur,
                        ...pfSansSigners,
                        id: cur.id,
                        sectionId: cur.sectionId,
                        // A signer row's `order` is a placeholder, not a real position: the
                        // binding's def snapshot omits `order` (snapshotSignerFieldDef), so
                        // generateSignaturesJSON emits 1 for every def-backed signer field.
                        // The section field's own order is the authority — otherwise the
                        // duplicate flattens a whole section to 1 on import, and the next
                        // publish writes those 1s back into formJSON. An attribute in both
                        // places is itself a defect: scripts/audit-section-signer-field-duplicates.js
                        // lists them.
                        order: cur.order ?? pfSansSigners.order,
                        metadata: mergedMeta,
                        annotationMultiline: annLines || cur.annotationMultiline,
                        editableAnnotation:
                            normalizeEditableAnnotation(pf.editableAnnotation) ?? cur.editableAnnotation,
                    };
                } else {
                    fields.push(publishedSignerOnlyFieldToClientField(pf, defaultSectionId, fields.length));
                }
            }
        }
    }
}

function publishedSignerOnlyFieldToClientField(pf, sectionId, orderIdx) {
    let metadata = pf.metadata ? { ...pf.metadata } : null;
    const pl = metadata?.pdfLocation;
    const annLines = pl && typeof pl === "object" && !Array.isArray(pl) ? pl.lines ?? 0 : 0;
    return {
        id: `field-import-signer-${Date.now()}-${orderIdx}-${Math.random().toString(36).slice(2, 9)}`,
        sectionId,
        type: pf.type || "input",
        // Do NOT default a field's label to its attributeName — leave it blank
        // so an author only sees a label they actually typed.
        label: pf.label === undefined ? "" : pf.label,
        attributeName: pf.attributeName,
        fieldNotes: pf.fieldNotes || "",
        placeholder: pf.placeholder ?? null,
        info: pf.info ?? null,
        hint: pf.hint ?? null,
        constraint: pf.constraint ?? null,
        constraintId: pf.constraintId ?? null,
        eventTrigger: pf.eventTrigger ?? null,
        value: pf.value ?? null,
        // Combined-deadline rollup keys. Signer-only fields (e.g. the
        // `Buyer1_SigDate_*` components tagged with combinedDate) carry these
        // ONLY here — there is no formData field to recover them from — so they
        // must survive the merge or they vanish on republish. Copied like
        // parseFields (undefined when absent; buildPublishedFieldOutput only
        // emits non-empty values).
        combinedDate: pf.combinedDate,
        combinedDateLabel: pf.combinedDateLabel,
        combinedTime: pf.combinedTime,
        combinedTimeLabel: pf.combinedTimeLabel,
        validation: pf.validation ?? {
            min: null,
            max: null,
            minLength: null,
            maxLength: null,
            patterns: null,
            required: null,
        },
        validationErrorMessage: pf.validationErrorMessage ?? null,
        metadata,
        readOnly: pf.readOnly ?? false,
        disabled: pf.disabled ?? null,
        customTemplateDoNotCopy: pf.customTemplateDoNotCopy ?? false,
        fieldData: pf.fieldData ?? null,
        labelFormUpload: pf.labelFormUpload ?? null,
        moreDetails: pf.moreDetails ?? null,
        order: pf.order ?? orderIdx + 1,
        annotationMultiline: annLines,
        isOverflow: null,
        editableAnnotation: normalizeEditableAnnotation(pf.editableAnnotation),
        // Signer-only fields (e.g. `Buyer1_SigDate`) live under
        // `signaturesJSON[role].signatures[i].fields[]` — they MUST
        // NOT also be emitted into `formJSON.formData.sections[]`
        // when we re-render the JSON. The builder's section iterator
        // skips fields with this flag (see formJsonBuilder.js
        // `excludeFromFormJSON` filter).
        excludeFromFormJSON: true,
    };
}

/**
 * Give each binding the client field it belongs to.
 *
 * Pairs the Nth binding for an attributeName with the Nth field of that name, rather than always
 * the first. A signer block may carry the same signer-prefixed attribute more than once — GA F522
 * places Buyer1_SigDate twice — and those import as separate client fields. Handing both bindings
 * the same linkedFieldId made generateSignaturesJSON group them into one row and merge their
 * rects, collapsing two boxes into one. One field per binding keeps them apart on republish.
 *
 * Falls back to the last matching field once the fields run out, which is the old behaviour for
 * the ordinary one-field case.
 */
export function augmentSignerBindingsWithLinkedFieldIds(bindings, fields) {
    if (!Array.isArray(bindings) || !Array.isArray(fields)) return;
    const usedByAttr = new Map();
    bindings.forEach((b) => {
        if (b.linkedFieldId) return;
        const matches = fields.filter((x) => x.attributeName === b.attributeName);
        if (matches.length === 0) return;
        const n = usedByAttr.get(b.attributeName) ?? 0;
        b.linkedFieldId = (matches[n] ?? matches[matches.length - 1]).id;
        usedByAttr.set(b.attributeName, n + 1);
    });
}

/**
 * Parse fields from form JSON
 */
function parseFields(formData, sections) {
    const allFields = [];
    const sections_data = formData.formJSON.formData[0].sections;
    
    sections_data.forEach((section, sectionIndex) => {
        const sectionId = sections[sectionIndex].id;
        
        if (!section.fields || section.fields.length === 0) {
            console.warn(`Section "${section.title}" has no fields`);
            return;
        }

        section.fields.forEach((field, fieldIndex) => {
            // Validate required field properties
            if (!field.type) {
                console.warn(`Field at section ${sectionIndex}, index ${fieldIndex} missing type`);
            }
            if (!field.attributeName) {
                console.warn(`Field at section ${sectionIndex}, index ${fieldIndex} missing attributeName`);
            }

            // Generate unique ID (preserve original if present, otherwise generate)
            const fieldId = field.id || `field-${Date.now()}-${sectionIndex}-${fieldIndex}`;

            // Ensure datetime fields have format in metadata.fieldData
            let metadata = field.metadata || null;
            if (field.type === "datetime") {
                if (!metadata) {
                    metadata = {};
                }
                if (!metadata.fieldData) {
                    metadata.fieldData = {};
                }
                // Ensure format is always present
                if (!metadata.fieldData.format) {
                    metadata.fieldData.format = "01/02/2006";
                }
            }

            // Enforce standard for checkboxWithOther and radioWithOther: input attributeName = value + "Input"
            if ((field.type === "checkboxWithOther" || field.type === "radioWithOther") && metadata?.other) {
                const checkboxOrRadioValue = metadata.other.checkbox?.value || metadata.other.radio?.value || "";
                if (checkboxOrRadioValue && metadata.other.input) {
                    // Enforce: input attributeName = value + "Input"
                    metadata.other.input.attributeName = `${checkboxOrRadioValue}Input`;
                }
            }

            // Overflow: expand metadata.overflow into synthetic fields; remove from parent metadata
            const overflowEntries = Array.isArray(metadata?.overflow) ? metadata.overflow : [];
            if (metadata && overflowEntries.length > 0) {
                const { overflow: _omit, ...metadataRest } = metadata;
                metadata = metadataRest;
            }

            const parentAttributeName = field.attributeName || `field${allFields.length}`;
            const parentOrder = field.order ?? fieldIndex;

            const mainField = {
                id: fieldId,
                sectionId: sectionId,
                type: field.type || "input",
                label: field.label === undefined ? "Untitled Field" : field.label,
                attributeName: parentAttributeName,
                fieldNotes: field.fieldNotes || "",
                placeholder: field.placeholder || null,
                info: field.info || null,
                hint: field.hint || null,
                constraint: field.constraint || null,
                constraintId: field.constraintId || null,
                eventTrigger: field.eventTrigger || null,
                value: field.value ?? null,
                validation: field.validation ?? {
                    min: null,
                    max: null,
                    minLength: null,
                    maxLength: null,
                    patterns: null,
                    required: null
                },
                validationErrorMessage: field.validationErrorMessage || null,
                metadata: metadata,
                readOnly: field.readOnly ?? false,
                disabled: field.disabled ?? null,
                customTemplateDoNotCopy: field.customTemplateDoNotCopy ?? false,
                fieldData: field.fieldData || null,
                labelFormUpload: field.labelFormUpload || null,
                moreDetails: field.moreDetails || null,
                dealSummary: field.dealSummary,
                dealSummaryLabel: field.dealSummaryLabel,
                dealSummaryType: field.dealSummaryType,
                formSummary: field.formSummary,
                formSummaryLabel: field.formSummaryLabel,
                formSummaryType: field.formSummaryType,
                combinedDate: field.combinedDate,
                combinedTime: field.combinedTime,
                combinedDateLabel: field.combinedDateLabel,
                combinedTimeLabel: field.combinedTimeLabel,
                order: parentOrder,
                annotationMultiline: metadata?.pdfLocation?.lines ?? 0,
                isOverflow: null,
                editableAnnotation: normalizeEditableAnnotation(field.editableAnnotation),
            };
            allFields.push(mainField);

            // Synthetic overflow fields (location-only in output; stored under target's metadata.overflow)
            overflowEntries.forEach((entry, index) => {
                const pdfLocation = entry?.pdfLocation || null;
                const attrName = entry?.attributeName || `${parentAttributeName}_overflow${index + 1}`;
                const syntheticId = `overflow-${fieldId}-${index}`;
                allFields.push({
                    id: syntheticId,
                    sectionId: sectionId,
                    type: "input",
                    label: `Overflow: ${attrName}`,
                    attributeName: attrName,
                    fieldNotes: "",
                    placeholder: null,
                    info: null,
                    hint: null,
                    constraint: null,
                    constraintId: null,
                    eventTrigger: null,
                    value: null,
                    validation: { min: null, max: null, minLength: null, maxLength: null, patterns: null, required: null },
                    validationErrorMessage: null,
                    metadata: pdfLocation ? { pdfLocation } : null,
                    readOnly: false,
                    customTemplateDoNotCopy: false,
                    fieldData: null,
                    labelFormUpload: null,
                    moreDetails: null,
                    dealSummary: undefined,
                    dealSummaryLabel: undefined,
                    dealSummaryType: undefined,
                    formSummary: undefined,
                    formSummaryLabel: undefined,
                    formSummaryType: undefined,
                    order: parentOrder + (index + 1) / 1000,
                    annotationMultiline: pdfLocation?.lines ?? 0,
                    isOverflow: parentAttributeName,
                });
            });
        });
    });

    return allFields;
}

/**
 * Reverse-transform output settings shape back to internal shape.
 * generateJson expands { type: { type: "isPurchaseContract" } } into { type: { isPurchaseContract: true } }.
 * This function reverses that so round-tripping through edit mode is stable.
 */
/**
 * Every form-type key the publish shape can carry. This list is an ALLOWLIST, not a hint: a key
 * missing from it makes normalizeSettings read the settings as already-internal, so nothing sets
 * `settings.type.type` and generateFormJson emits no `type` at all — the flag is silently dropped
 * on the next publish. Add new type keys here AND to the Type radio list in FormDetailsSelector.
 */
const TYPE_KEYS = [
    "isPurchaseContract",
    "isPurchaseContractCounter",
    "isPurchaseContractAmend",
    "isPurchaseContractAddendum",
    "isChangeOfStatus",
    "isClientListingContract",
    "isClientListingContractAmend",
    "isCustomAddendum",
    "isPurchaseContractAgreementToRevive",
    // No isExtensionOrTerm: the one-sided extend-or-terminate form's identity is
    // isPurchaseContractAmend, and the forms that gate on it find it through the
    // reqs.isExtensionOrTermTarget marker instead.
    "isExtensionOrTerm_2SC",
    "isInspectionObjectionNotice",
    "isInspectionObjectionNoticeWithdrawal",
    "isInspectionResolution",
    "isAppraisalObjectionNotice",
    "isAppraisalObjectionNoticeWithdrawal",
    "isTitleRelatedObjectionNotice",
    "isTitleRelatedObjectionNoticeWithdrawal",
    "isSellerNetSheet",
];
const PROPERTY_TYPE_KEYS = [
    "isResidential",
    "isCommercial",
    "isLand",
    "isManufactured",
    "isComplianceForm",
    "isBuilder",
    "isNewConstruction",
];

/**
 * Rule flags. These live in the same published `settings.type` dictionary as the keys above,
 * but they are ADDITIVE rather than an identity choice: they describe how a form behaves and
 * sit alongside whichever type/propertyType the form already has (e.g. CO's Amend Listing is
 * `isClientListingContractAmend` AND `allowsMultipleSignedInstances`). So they cannot be radio
 * options — internally they are held as `settings.type.rules`, a small boolean map, and folded
 * back into `settings.type` on publish. Same allowlist warning as TYPE_KEYS: a rule key missing
 * from here is dropped on the next publish. Add new rule keys here AND to the Rules checkbox
 * list in FormDetailsSelector.
 */
const RULE_KEYS = ["actionExecutedTerminatesDeal", "allowsMultipleSignedInstances"];

export function parseSettings(outputSettings) {
    if (!outputSettings || typeof outputSettings !== "object") return {};

    const settings = {};

    // Reverse type expansion
    if (outputSettings.type && typeof outputSettings.type === "object") {
        const typeKeys = Object.keys(outputSettings.type);
        const matchedType = typeKeys.find((k) => TYPE_KEYS.includes(k) && outputSettings.type[k]);
        const matchedPropertyType = typeKeys.find((k) => PROPERTY_TYPE_KEYS.includes(k) && outputSettings.type[k]);
        // Rules are additive, so collect every one that's set rather than the first match.
        const matchedRules = typeKeys.filter((k) => RULE_KEYS.includes(k) && outputSettings.type[k]);

        if (matchedType || matchedPropertyType || matchedRules.length) {
            settings.type = {};
            if (matchedType) settings.type.type = matchedType;
            if (matchedPropertyType) settings.type.propertyType = matchedPropertyType;
            if (matchedRules.length) {
                settings.type.rules = Object.fromEntries(matchedRules.map((k) => [k, true]));
            }
        }
    }

    // reqs passes through as-is
    if (outputSettings.reqs && Object.keys(outputSettings.reqs).length > 0) {
        settings.reqs = outputSettings.reqs;
    }

    // Plain boolean flags with no internal shape to reverse. They must be copied, not left to
    // the builder's defaults: `pdfV2 !== false` would turn an explicit `pdfV2: false` back into
    // true, and sigOnly/shareOnly have no default at all, so a form that also carries a `type`
    // (the only shape that reaches this function) would lose them on republish.
    if (outputSettings.pdfV2 === false) settings.pdfV2 = false;
    if (outputSettings.sigOnly === true) settings.sigOnly = true;
    if (outputSettings.shareOnly === true) settings.shareOnly = true;

    // Reverse daysAfter expansion.
    // Publish uses mecLabel for both MEC ("MEC") and BAD ("BAD"); there is no badLabel key.
    if (outputSettings.daysAfter && typeof outputSettings.daysAfter === "object") {
        const da = outputSettings.daysAfter;
        const daysAfter = {};
        if (da.mecLabel) {
            const label = String(da.mecLabel).trim().toUpperCase();
            if (label === "BAD") {
                daysAfter.bad = true;
                if (da.mecPreposition) daysAfter.badPreposition = da.mecPreposition;
            } else if (label === "MEC") {
                daysAfter.mec = true;
                if (da.mecPreposition) daysAfter.mecPreposition = da.mecPreposition;
            }
        }
        if (da.absLabel) {
            daysAfter.bs = true;
            if (da.absPreposition) daysAfter.bsPreposition = da.absPreposition;
        }
        if (da.coeLabel) {
            daysAfter.coe = true;
            if (da.coePreposition) daysAfter.coePreposition = da.coePreposition;
        }
        if (da.dbcLabel) {
            daysAfter.dbc = true;
            if (da.dbcPreposition) daysAfter.dbcPreposition = da.dbcPreposition;
        }
        // The afterFormExecuted anchor has no UI: it is authored in the JSON and must survive the
        // round-trip untouched. Carried verbatim rather than reduced to a flag, so whatever the
        // backend adds to mecAnchorTarget comes back out the same shape it went in.
        if (da.afterFormExecutedInfo != null) daysAfter.afterFormExecutedInfo = da.afterFormExecutedInfo;
        if (da.afterFormExecutedPreposition != null) {
            daysAfter.afterFormExecutedPreposition = da.afterFormExecutedPreposition;
        }
        if (da.mecAnchorTarget && typeof da.mecAnchorTarget === "object") {
            daysAfter.mecAnchorTarget = da.mecAnchorTarget;
        }
        if (Object.keys(daysAfter).length > 0) {
            settings.daysAfter = daysAfter;
        }
    }

    // brandedForms (e.g. { maxHeight, maxWidth }) has no UI — pass through as-is
    // so it survives the settings reverse-transform on JSON-preview round-trip.
    if (outputSettings.brandedForms && typeof outputSettings.brandedForms === "object") {
        settings.brandedForms = outputSettings.brandedForms;
    }

    return settings;
}

/**
 * Move a type flag written at the top level of `settings` into `settings.type`, where the
 * publish shape puts it. These bundles are hand-edited, and `settings: { isLand: true }` plainly
 * means `settings: { type: { isLand: true } }` — read it that way rather than dropping the flag
 * for being in the wrong place. Input is taken as written; output is always canonical.
 */
function hoistTopLevelTypeKeys(settings) {
    const known = [...TYPE_KEYS, ...PROPERTY_TYPE_KEYS, ...RULE_KEYS];
    const misplaced = Object.keys(settings).filter((k) => known.includes(k));
    if (misplaced.length === 0) return settings;

    const hoisted = { ...settings };
    const type = { ...(settings.type && typeof settings.type === "object" ? settings.type : {}) };
    for (const key of misplaced) {
        if (settings[key]) type[key] = true;
        delete hoisted[key];
    }
    hoisted.type = type;
    return hoisted;
}

/**
 * Detect whether settings is in output (expanded) format and normalize to internal format.
 * Internal format: { type: { type: "isPurchaseContract", propertyType: "isResidential" }, daysAfter: { mec: true } }
 * Output format:   { type: { isPurchaseContract: true, isResidential: true }, daysAfter: { mecLabel: "MEC", ... } }
 */
function normalizeSettings(rawSettings) {
    if (!rawSettings || typeof rawSettings !== "object") return {};

    const settings = hoistTopLevelTypeKeys(rawSettings);

    // Check if settings.type is in output format (has boolean values for known type keys).
    // RULE_KEYS count too: CO's Notice to Terminate publishes `type: { actionExecutedTerminatesDeal:
    // true }` and nothing else, so without them here it reads as already-internal and the flag is lost.
    const isOutputFormat = settings.type &&
        typeof settings.type === "object" &&
        Object.keys(settings.type).some((k) => [...TYPE_KEYS, ...PROPERTY_TYPE_KEYS, ...RULE_KEYS].includes(k));

    // Check if daysAfter is in output format (has label keys like mecLabel)
    const isDaysAfterOutputFormat = settings.daysAfter &&
        typeof settings.daysAfter === "object" &&
        Object.keys(settings.daysAfter).some((k) => ["mecLabel", "absLabel", "coeLabel", "dbcLabel"].includes(k));

    if (isOutputFormat || isDaysAfterOutputFormat) {
        return parseSettings(settings);
    }

    return settings;
}

/**
 * Converts published `additionalFieldsJSON` to client `additionalFields` rows (stable ids for session).
 * Accepts export array `[{ attributeName, pdfLocation: [...] }]`, legacy map, or legacy single-object `pdfLocation`.
 */
export function additionalFieldsJSONToClientRows(rawAdditional) {
    const entries = normalizeAdditionalFieldsJSONEntries(rawAdditional);
    const ts = Date.now();
    let addIdx = 0;
    const additionalRows = [];
    entries.forEach((entry) => {
        const name = entry.attributeName ?? "";
        const locations = entry.pdfLocation || [];
        if (locations.length === 0) {
            additionalRows.push({
                id: `additional-${addIdx++}-${ts}`,
                attributeName: name,
                pdfLocation: null,
                lines: 0,
            });
            return;
        }
        locations.forEach((loc) => {
            additionalRows.push({
                id: `additional-${addIdx++}-${ts}`,
                attributeName: name,
                pdfLocation: loc ?? null,
                lines: loc && typeof loc === "object" && !Array.isArray(loc) ? loc.lines ?? 0 : 0,
            });
        });
    });
    return additionalRows;
}

/**
 * subsequentSigningJSON → Studio state, verbatim.
 *
 * The block is shaped exactly like signaturesJSON — an object keyed by meviDealRole whose signer
 * rows carry `signerKey`, `email` and their own `fields[]` — plus `label`, `actionLabel` and
 * `key` at the role level. Studio carries those three; they are authored in JSON and edited
 * through JSON Preview, not the settings panel.
 *
 * Legacy array shapes are gone. A bundle still holding one parses to no subsequent signing at all
 * rather than being silently reshaped into something publishable.
 *
 * @returns {Object} role-keyed blocks, or {} when there are none
 */
export function parseSubsequentSigningJSON(raw) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
    return JSON.parse(JSON.stringify(raw));
}

/**
 * Parse metadata (form details, roles, creators, settings)
 */
function parseMetadata(formData) {
    // Extract formId from top-level id field
    const formId = formData.id || formData.formJSON?.id || "00000000-0000-0000-0000-000000000000";
    
    // Extract order from top-level metadata. Coerced to a number so forms
    // previously saved with a string order ("20") heal on load.
    const rawOrder = formData.metadata?.order;
    const order =
        rawOrder === null || rawOrder === undefined || rawOrder === ""
            ? null
            : Number.isFinite(Number(rawOrder))
                ? Number(rawOrder)
                : null;
    
    const metadata = {
        formDetails: {
            locale: formData.formJSON?.locale || "",
            type: formData.formJSON?.type || "",
            name: formData.formJSON?.name || "",
            shortName: formData.formJSON?.shortName || "",
            category: formData.metadata?.category || "",
            formId: formId,
            version: formData.version || "",
            otherSideCanUpload: formData.metadata?.otherSideCanUpload ?? true,
            settings: normalizeSettings(formData.metadata?.settings),
            dataExtraction: formData.metadata?.dataExtraction || null,
            lineNumbers: formData.formJSON?.lineNumbers || false,
            // Preserve-only (no studio UI). Dropping these on a round-trip
            // silently loses authored data — see
            // documentation/patterns/round-trip-preservation.md.
            transactionBroker: formData.metadata?.transactionBroker ?? null,
            // formData[0]-level decision blocks. Authored in JSON with no
            // studio UI, so they are carried verbatim like `rulesJSON`.
            // Where the form begins. Usually the first section, but not
            // always (CO 0021 starts at its THIRD section), and the builder
            // used to overwrite it with sections[0] unconditionally.
            startSectionAttributeName: formData.formJSON?.startSectionAttributeName ?? null,
            copySectionsIfDecision: formData.formJSON?.formData?.[0]?.copySectionsIfDecision ?? null,
            compareForNoChangeOrDeleted:
                formData.formJSON?.formData?.[0]?.compareForNoChangeOrDeleted ?? null,
            signatureAddendum: formData.formJSON?.signatureAddendum ?? null,
            signatureAddendumSources: formData.formJSON?.signatureAddendumSources ?? null,
            // Cascade defaults the v2 viewer module reads (font,
            // glyphs, edit/locked backgrounds, etc.). Stored as-is so
            // the builder can re-emit verbatim — the studio doesn't
            // currently surface UI for editing the cascade, but the
            // value still has to round-trip when one is present.
            annotationStyles: formData.metadata?.annotationStyles ?? null,
            metadata: {
                order: order
            }
        },
        signatures: {},
        initials: {},
        signerAttributeBindings: [],
        initialerAttributeBindings: [],
        initialsAnnotations: [],
        selectedCreators: formData.metadata?.meviDealRoleCreator || [],
        formUploadSections: []
    };

    // rulesJSON is authored in JSON only — Studio carries it verbatim and never
    // derives, reshapes, or regenerates it. Studio previously kept just the
    // `locked.setDesignatedSigners.contract` order and rebuilt the rest on publish,
    // which flattened every other rule the JSON declared.
    if (formData.rulesJSON && typeof formData.rulesJSON === "object" && !Array.isArray(formData.rulesJSON)) {
        metadata.rulesJSON = JSON.parse(JSON.stringify(formData.rulesJSON));
    }

    // Parse signatures from signaturesJSON only. A template with no signaturesJSON has no
    // signers — Studio does not invent them from rulesJSON, which is JSON-authored data
    // Studio only carries.
    if (formData.signaturesJSON && typeof formData.signaturesJSON === "object" && Object.keys(formData.signaturesJSON).length > 0) {
        const rawSigs = formData.signaturesJSON;
        metadata.signerAttributeBindings = extractSignerAttributeBindingsFromSignatures(rawSigs);
        /** Keep embedded signer `fields` in Studio state so export can round-trip if bindings are missing (e.g. stale API payload). */
        metadata.signatures = JSON.parse(JSON.stringify(rawSigs));
    }

    const rawInitials = formData.formJSON?.initialsJSON ?? formData.initialsJSON;
    if (rawInitials && typeof rawInitials === "object" && Object.keys(rawInitials).length > 0) {
        metadata.initials = JSON.parse(JSON.stringify(rawInitials));
        metadata.initialerAttributeBindings = extractSignerAttributeBindingsFromSignatures(rawInitials).map((b) => ({
            ...b,
            id: b.id?.replace(/^signer-bind-/, "initialer-bind-") || b.id,
        }));
        const ts = Date.now();
        let annIdx = 0;
        for (const [roleKey, roleValue] of Object.entries(rawInitials)) {
            const sigs = signerRowsOf(roleValue);
            sigs.forEach((sig) => {
                if (!sig?.signerKey) return;
                // One annotation per rect — a signer may initial in several places.
                tabRectsOf(sig).forEach((pdfLocation) => {
                    metadata.initialsAnnotations.push({
                        id: `initials-${roleKey}-${sig.signerKey}-${annIdx++}-${ts}`,
                        roleKey,
                        signerKey: sig.signerKey,
                        pdfLocation,
                    });
                });
            });
        }
    }

    // Parse form upload sections from metadata
    const formUploadDataArrays = collectFormUploadDataArrays(formData.metadata);
    const formUploadSettings = formData.metadata?.formUploadSettings || {};

    // One loop for however many arrays the template carries — previously three
    // hardcoded blocks, so a fourth upload section was silently discarded.
    formUploadDataArrays.forEach((attrNames, idx) => {
        if (!attrNames.length) return;
        const settings = formUploadSettings[`formUploadData${idx + 1}`] || {};
        metadata.formUploadSections.push({
            id: `upload-section-${idx + 1}`,
            title: settings.title || `Form Upload Section ${idx + 1}`,
            description: settings.description || "",
            fieldIds: [] // Will be populated after fields are created
        });
    });

    // additionalFieldsJSON (top-level sibling to signaturesJSON) -> additionalFields with client-only id
    const rawAdditional = formData.additionalFieldsJSON ?? formData.formJSON?.additionalFieldsJSON;
    metadata.additionalFields = additionalFieldsJSONToClientRows(rawAdditional);

    // Carried verbatim, exactly like signaturesJSON above — including the PDF placements, which
    // are pulled out into bindings the same way so subsequent fields are editable on the PDF.
    metadata.subsequentSigning = parseSubsequentSigningJSON(
        formData.formJSON?.subsequentSigningJSON ?? formData.subsequentSigningJSON,
    );
    metadata.subsequentSigningAttributeBindings = extractSignerAttributeBindingsFromSignatures(
        metadata.subsequentSigning,
    ).map((b) => ({
        ...b,
        id: b.id?.replace(/^signer-bind-/, "subsequent-bind-") || b.id,
    }));

    return metadata;
}

/**
 * Validate imported fields against existing fields (for duplicate detection)
 */
export function validateFieldUniqueness(importedFields, existingFields = []) {
    const warnings = [];
    const attributeNames = new Set();
    
    // Check for duplicates within imported fields
    importedFields.forEach((field, index) => {
        const attrName = field.attributeName.toLowerCase();
        if (attributeNames.has(attrName)) {
            warnings.push(`Duplicate attributeName "${field.attributeName}" found at field index ${index}`);
        }
        attributeNames.add(attrName);
    });

    // Check against existing fields
    existingFields.forEach(existing => {
        const attrName = existing.attributeName.toLowerCase();
        if (attributeNames.has(attrName)) {
            warnings.push(`Imported field "${existing.attributeName}" conflicts with existing field`);
        }
    });

    return warnings;
}
